// btw-front/src/api/partnerDeals.js
import { api, getAccessToken } from "./client";

export async function list({ status, q, mine } = {}) {
  const params = new URLSearchParams();
  if (status && status !== "all") params.set("status", status);
  if (q) params.set("q", q);
  if (mine) params.set("mine", "true");
  const qs = params.toString();
  const res = await api.get(`/partner/deals${qs ? `?${qs}` : ""}`);
  return res.items || [];
}

// 전사 딜 검색 — 결과 항목은 마스킹된 파트너사명·상태만 포함(상세 진입 불가)
export async function search({ q } = {}) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  const qs = params.toString();
  const res = await api.get(`/partner/deals/search${qs ? `?${qs}` : ""}`);
  return res.items || [];
}

export async function get(id) {
  return api.get(`/partner/deals/${encodeURIComponent(id)}`);
}

export async function create(payload) {
  return api.post("/partner/deals", payload);
}

// 소프트 삭제 — reason(선택)은 관리자 [삭제된 딜] 열람 페이지에 표시
export async function remove(id, reason) {
  return api.del(`/partner/deals/${encodeURIComponent(id)}`, { body: { reason: reason || "" } });
}

export async function update(id, payload) {
  return api.put(`/partner/deals/${encodeURIComponent(id)}`, payload);
}

export async function getMe() {
  return api.get("/partner/me");
}

// 회사 규모(영업대표/기술지원 인원수) 수정
export async function updateMyCompany(payload) {
  return api.put("/partner/company", payload);
}

// 견적서 업로드 (multipart). file.name 을 별도 form field 로 같이 전송 — multer
// originalname 인코딩 손상 시 서버가 이 값을 우선 사용해 한국어 파일명 보존.
export async function uploadQuotation(id, file) {
  const fd = new FormData();
  fd.append("quotation", file);
  fd.append("filename", file.name);
  return api.postForm(`/partner/deals/${encodeURIComponent(id)}/quotation`, fd);
}

// 견적서 다운로드 — Authorization 헤더 필요해 fetch 직접 사용 후 Blob 처리
export async function downloadQuotation(id, suggestedName) {
  const token = getAccessToken();
  const res = await fetch(`/api/partner/deals/${encodeURIComponent(id)}/quotation`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    // api 레이어 영문 폴백 — 호출자가 i18n 폴백으로 덮어쓴다.
    let msg = "Download failed";
    try {
      const j = await res.json();
      msg = (j && j.error && j.error.message) || msg;
    } catch (e) {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = suggestedName || "quotation";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

// 도입(adoption) 전환 — multipart: files[](≥1, 기존 증빙 파일이 있으면 생략 가능) + partnerMemo(선택).
// 서버가 status 변경·처리자 기록·파일 저장을 한 트랜잭션으로 처리. 응답 { ok, adoptionFiles[] }.
export async function adopt(id, { files, partnerMemo } = {}) {
  const fd = new FormData();
  Array.from(files || []).forEach((f) => fd.append("files", f, f.name));
  if (partnerMemo) fd.append("partnerMemo", partnerMemo);
  return api.postForm(`/partner/deals/${encodeURIComponent(id)}/adoption`, fd);
}

// 증빙 파일 추가 — 도입 처리한 본인만(서버 403). 응답 { adoptionFiles[] }.
export async function uploadAdoptionFiles(id, files) {
  const fd = new FormData();
  Array.from(files || []).forEach((f) => fd.append("files", f, f.name));
  return api.postForm(`/partner/deals/${encodeURIComponent(id)}/adoption-files`, fd);
}

// 증빙 파일 삭제 — 도입 처리한 본인만. 도입 상태면 마지막 1개 삭제 불가(서버 400). 응답 { adoptionFiles[] }.
export async function removeAdoptionFile(id, fileId) {
  return api.del(
    `/partner/deals/${encodeURIComponent(id)}/adoption-files/${encodeURIComponent(fileId)}`,
  );
}

// 증빙 파일 다운로드 — Authorization 헤더 필요해 fetch 직접 사용 후 Blob 처리(견적서 패턴 동일)
export async function downloadAdoptionFile(id, fileId, suggestedName) {
  const token = getAccessToken();
  const res = await fetch(
    `/api/partner/deals/${encodeURIComponent(id)}/adoption-files/${encodeURIComponent(fileId)}`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  if (!res.ok) {
    let msg = "Download failed";
    try {
      const j = await res.json();
      msg = (j && j.error && j.error.message) || msg;
    } catch (e) {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = suggestedName || "file";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

// 관리자 메시지 목록 (열람 전용) — { items: [...] }, 메일 상태는 서버가 내려주지 않음
export async function listMessages(id) {
  const res = await api.get(`/partner/deals/${encodeURIComponent(id)}/messages`);
  return res.items || [];
}

// 본인 이름·연락처·비밀번호 수정 — 보낸 필드만 바뀐다(이메일·로그인 ID 는 관리자 경로)
export async function updateMe(payload) {
  return api.put("/partner/me", payload);
}
